// /src/components/ReservationModal.jsx
// Modal dedicato per la PRENOTAZIONE DEGUSTAZIONE (variante Group).
// Differisce dal LeadFormModal perché raccoglie data + numero persone,
// e spara l'evento Standard Meta "Schedule" (Prenotazione) anziché "Lead".
import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { trackEventDedup, persistLeadData } from '../lib/tracking';

const INITIAL_FORM = { nome: '', telefono: '', persone: '4', data: '', privacy: false };

const ReservationModal = ({ open, onClose, source = 'lp-degustazione' }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const startedRef = useRef(false);

  // FormView tracking — fire-and-forget, no setState (state is reset via `key` prop on the parent).
  useEffect(() => {
    if (open) trackEventDedup('FormView', { source, form_type: 'reservation' });
  }, [open, source]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const onChange = (field) => (e) => {
    const value = field === 'privacy' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
    if (!startedRef.current) {
      startedRef.current = true;
      trackEventDedup('FormStart', { source, first_field: field, form_type: 'reservation' });
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  const validate = () => {
    if (!form.nome.trim()) return 'Inserisci il tuo nome.';
    const phone = form.telefono.replace(/[\s().-]/g, '');
    if (!/^\+[1-9]\d{6,14}$/.test(phone)) return 'Inserisci il telefono con prefisso internazionale (es. +393331234567).';
    if (!form.data) return 'Scegli una data per la prenotazione.';
    if (form.data < today) return 'La data deve essere oggi o successiva.';
    const p = parseInt(form.persone, 10);
    if (!p || p < 2 || p > 30) return 'Numero persone non valido (2-30).';
    if (!form.privacy) return 'Devi accettare la Privacy Policy.';
    return '';
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setErrorMsg(err); return; }
    setErrorMsg(''); setSubmitting(true);

    const payload = {
      first_name: form.nome.trim(),
      last_name: '',
      email: '',
      phone: form.telefono.replace(/[\s().-]/g, ''),
      privacy_consent: form.privacy,
      source,
      offer: 'Degustazione Sfarinà',
      reservation_date: form.data,
      reservation_people: parseInt(form.persone, 10),
      form_type: 'reservation',
    };

    try {
      // Schedule è uno Standard Event Meta: ottimo per ottimizzazione campagne
      await trackEventDedup('Schedule', payload);
      persistLeadData(payload);
      setSuccess(true);
    } catch (e2) {
      setErrorMsg('Si è verificato un errore. Riprova tra qualche istante.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="reservation-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[2147483647] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative bg-[#FDFBF7] w-full max-w-lg rounded-2xl shadow-2xl border border-[#EDE8E0] overflow-hidden animate-[fadeIn_.25s_ease-out]">
        <button type="button" onClick={onClose} aria-label="Close" data-testid="reservation-close"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white hover:bg-[#EDE8E0] border border-[#EDE8E0] flex items-center justify-center">
          <X size={18} className="text-[#1A1A1A]" />
        </button>

        <div className="bg-[var(--color-accent,#b03121)] text-white px-6 py-7">
          <p className="font-['Poppins',_sans-serif] uppercase text-xs tracking-[0.2em] opacity-90 mb-2">Posti limitati</p>
          <h3 className="font-['Poppins',_sans-serif] font-black text-2xl md:text-3xl leading-tight">
            Prenota la tua<br /><span className="italic">Degustazione Sfarinà</span>
          </h3>
        </div>

        <div className="p-6 md:p-8">
          {success ? (
            <div data-testid="reservation-success" className="text-center py-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-[var(--color-accent,#b03121)]/10 border-2 border-[var(--color-accent,#b03121)] flex items-center justify-center mb-5">
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--color-accent, #b03121)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <h4 className="font-['Poppins',_sans-serif] font-bold text-2xl text-[#1A1A1A] mb-3">Grazie {form.nome}!</h4>
              <p className="font-['Lora',_serif] text-[#333] text-base leading-relaxed mb-6">
                Abbiamo registrato la richiesta per <span className="font-bold">{form.persone} persone</span> il <span className="font-bold">{form.data}</span>.<br />
                Ti contatteremo a breve su WhatsApp per confermare la prenotazione.
              </p>
              <button type="button" onClick={onClose} data-testid="reservation-success-close"
                className="inline-flex items-center justify-center bg-[#1A1A1A] hover:bg-[#333] text-white font-['Poppins',_sans-serif] font-bold py-3 px-8 rounded-lg transition-colors">
                Chiudi
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="reservation-form" noValidate>
              <p className="font-['Lora',_serif] text-[#5a4a42] text-sm md:text-base mb-5">
                Lascia i tuoi dati e ti contattiamo su WhatsApp per confermare.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block font-['Poppins',_sans-serif] font-semibold text-sm text-[#1A1A1A] mb-1.5">Nome e Cognome</label>
                  <input data-testid="reservation-nome" type="text" value={form.nome} onChange={onChange('nome')} required
                    className="w-full px-4 py-3 rounded-lg bg-white border border-[#E0D5C1] focus:border-[var(--color-accent,#b03121)] focus:ring-2 focus:ring-[var(--color-accent,#b03121)]/20 outline-none" />
                </div>
                <div>
                  <label className="block font-['Poppins',_sans-serif] font-semibold text-sm text-[#1A1A1A] mb-1.5">Telefono</label>
                  <input data-testid="reservation-telefono" type="tel" value={form.telefono} onChange={onChange('telefono')} placeholder="+393331234567" required
                    className="w-full px-4 py-3 rounded-lg bg-white border border-[#E0D5C1] focus:border-[var(--color-accent,#b03121)] focus:ring-2 focus:ring-[var(--color-accent,#b03121)]/20 outline-none" />
                </div>
                <div>
                  <label className="block font-['Poppins',_sans-serif] font-semibold text-sm text-[#1A1A1A] mb-1.5">Numero persone</label>
                  <input data-testid="reservation-persone" type="number" min={2} max={30} value={form.persone} onChange={onChange('persone')} required
                    className="w-full px-4 py-3 rounded-lg bg-white border border-[#E0D5C1] focus:border-[var(--color-accent,#b03121)] focus:ring-2 focus:ring-[var(--color-accent,#b03121)]/20 outline-none" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block font-['Poppins',_sans-serif] font-semibold text-sm text-[#1A1A1A] mb-1.5">Data desiderata</label>
                  <input data-testid="reservation-data" type="date" min={today} value={form.data} onChange={onChange('data')} required
                    className="w-full px-4 py-3 rounded-lg bg-white border border-[#E0D5C1] focus:border-[var(--color-accent,#b03121)] focus:ring-2 focus:ring-[var(--color-accent,#b03121)]/20 outline-none" />
                </div>
              </div>
              <label className="flex items-start gap-3 mt-5 cursor-pointer">
                <input type="checkbox" data-testid="reservation-privacy" checked={form.privacy} onChange={onChange('privacy')} className="mt-1 w-5 h-5 cursor-pointer" style={{ accentColor: 'var(--color-accent, #b03121)' }} />
                <span className="font-['Lora',_serif] text-xs md:text-sm text-[#5a4a42] leading-snug">
                  Acconsento al trattamento dei dati personali per ricevere la conferma della prenotazione e comunicazioni promozionali da Sfarinà.
                </span>
              </label>
              {errorMsg && (
                <div data-testid="reservation-error" className="mt-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 font-['Poppins',_sans-serif] text-sm font-semibold">
                  {errorMsg}
                </div>
              )}
              <button type="submit" disabled={submitting} data-testid="reservation-submit"
                className="mt-6 w-full inline-flex items-center justify-center bg-[var(--color-accent,#b03121)] hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed text-white font-['Poppins',_sans-serif] font-bold py-4 px-8 rounded-lg shadow-lg transition-all">
                {submitting ? 'Invio in corso…' : 'Prenota ora la tua degustazione'}
              </button>
              <p className="mt-4 text-center text-xs text-[#5a4a42] font-['Lora',_serif]">I tuoi dati sono al sicuro. Puoi disiscriverti in ogni momento.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReservationModal;
